export type CallState = 'connecting' | 'connected' | 'reconnecting' | 'ended' | 'failed' | 'ringing';

export const MEETING_ROOM_PREP_MS = 5000;

export type AttachCapableTrack = {
  attach: (element?: HTMLMediaElement) => HTMLMediaElement | HTMLMediaElement[];
};

export const getErrorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : String(error);

// Host is minted into the LiveKit token metadata (signed server-side). The
// identity-prefix heuristic is kept as a fallback for tokens issued before
// the metadata flag existed.
export const participantIsHost = (participant: { identity: string; metadata?: string } | undefined): boolean => {
  if (!participant) return false;
  if (participant.metadata) {
    try {
      const meta = JSON.parse(participant.metadata) as { isHost?: boolean };
      if (typeof meta.isHost === 'boolean') return meta.isHost;
    } catch {
      // malformed metadata — fall through to legacy heuristic
    }
  }
  return participant.identity.startsWith('user-');
};

export interface Participant {
  identity: string;
  name: string;
  isSpeaking: boolean;
  isMuted: boolean;
  hasVideo: boolean;
  /** True when the participant is publishing a screen-share video track. */
  hasScreenShare?: boolean;
  isHost?: boolean;
  isHandRaised?: boolean;
  /** Epoch ms when the hand was raised — used to order the question queue. */
  handRaisedAt?: number;
  joinedAt?: Date;
  avatar_url?: string;
}

export interface ActivityEntry {
  id: string;
  type: 'join' | 'leave' | 'hand_raised' | 'hand_lowered' | 'mute' | 'unmute' | 'reaction';
  participantName: string;
  participantId: string;
  timestamp: Date;
  emoji?: string;
}

export interface WaitingRoomGuest {
  guest_identity: string;
  guest_name: string;
  guest_email?: string | null;
  requested_at?: string;
}

export interface ChatMessage {
  id: string;
  identity: string;
  name: string;
  text: string;
  timestamp: Date;
  isSelf: boolean;
  /** Marked by the sender as a question for the host/presenter. */
  isQuestion?: boolean;
}

/** Payload sent over the LiveKit data channel for a chat message. */
export interface ChatMessagePayload {
  id: string;
  identity: string;
  name: string;
  text: string;
  timestamp: Date;
  isQuestion?: boolean;
}

export const MAX_CHAT_MESSAGES = 200;
export const MAX_CHAT_MESSAGE_LENGTH = 1000;

/** Host-grantable permissions a guest/participant can request over the data channel. */
export type CallPermission = 'screen_share' | 'whiteboard';

export interface PermissionRequestPayload {
  type: 'permission_request';
  permission: CallPermission;
  name: string;
}

export interface PermissionResponsePayload {
  type: 'permission_response';
  permission: CallPermission;
  granted: boolean;
}

export type PermissionRequestStatus = 'pending' | 'approved' | 'denied';

export interface PendingPermissionRequest {
  identity: string;
  permission: CallPermission;
  name: string;
  status: PermissionRequestStatus;
  requestedAt: number;
}

export type DocumentPresentationMode = 'pdf' | 'image' | 'office' | 'text' | 'embed';

export interface DocumentPresentationState {
  /** Public (guest-accessible) URL serving the file content. */
  url: string;
  /** Public URL of the full share page — used by 'embed' mode and as an open link. */
  sharePageUrl: string;
  mimeType: string;
  fileName: string;
  extension: string;
  mode: DocumentPresentationMode;
  presenterIdentity: string;
  presenterName: string;
}

export type PresentationNavDirection = 'next' | 'prev' | 'goto';

export interface PresentationNavPayload {
  direction: PresentationNavDirection;
  page?: number;
}

export interface PresentationScrollPayload {
  /** Scroll offset as a fraction of total scrollable depth. */
  scrollTopRatio: number;
  /** Visible window as a fraction of total scrollable depth. */
  viewportRatio: number;
}

/** A participant's laser-pointer position over shared content (normalized 0..1). */
export interface RemotePointer {
  identity: string;
  name: string;
  x: number;
  y: number;
  updatedAt: number;
}

export const isScreenShareRequestAllowed = (isGuestIdentity: boolean): boolean =>
  isGuestIdentity;

export const guestIdentityFromLocal = (identity: string): boolean =>
  identity.startsWith('guest-');

export const CALL_REACTIONS = [
  { emoji: '👍', label: 'Like' },
  { emoji: '❤️', label: 'Love' },
  { emoji: '😂', label: 'Laugh' },
  { emoji: '😮', label: 'Wow' },
  { emoji: '👏', label: 'Clap' },
  { emoji: '🎉', label: 'Celebrate' },
  { emoji: '🙌', label: 'Raise the roof' },
  { emoji: '🔥', label: 'Fire' },
] as const;

export const formatDuration = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};
